import {Component, ElementRef, HostListener, Input, OnInit, inject, forwardRef} from '@angular/core';
import {MySitesService} from '../../services/my-sites.service';
import {distinctUntilChanged, finalize} from 'rxjs/operators';
import {Router} from '@angular/router';
import {SitesQueryResponse} from '../../types/site.type';
import {MySitesApiService} from '../../services/api/my-sites-api.service';
import {AccessibilitySolutionService} from '../../services/accessibility-solution.service';
import {environment} from '../../../environments/environment';
import {SitesQuery} from 'src/app/common/elf-store/sites/sites.query';
import {PaginationService} from 'src/app/services/pagination.service';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {SUBSCRIPTIONS} from '../../common/utils/constants';
import {AutoUnsubscribe} from '../../decorators/auto-unsubscribe.decorator';
import {UserPermissions} from 'src/app/common/elf-store/user-permissions/user-permissions.store';
import {
  ISiteStatus,
  SiteSearchEntryDto,
  SiteSearchResultDto,
  UwV2SitesSearchListParams,
} from '../../../userway/sdk/UserWayApiClient';
import {AmplitudeService} from '../../services/amplitude.service';
import {AmplitudeManager} from '../../common/elf-store/amplitude/amplitude.manager';
import {WalkThroughStepDirective} from '../walkthrough/walkthough-step/walkthrough-step.directive';
import {SearchInputComponent} from '../../components/dumb/search-input/search-input.component';
import {BlurOnComponentDirective} from '../../common/directives/blur-on-component.directive';
import {TooltipPanelWrapperComponent} from '../../components/dumb/tooltip-panel-wrapper/tooltip-panel-wrapper.component';
import {TooltipPanelComponent} from '../../components/dumb/tooltip-panel/tooltip-panel.component';
import {NgClass} from '@angular/common';
import {ProgressSpinnerComponent} from '../../components/dumb/progress-spinner/progress-spinner.component';
import {SiteListComponent} from './site-list/site-list.component';
import {BtnComponent} from '../../components/dumb/button/button.component';
import {PermissionGuardDirective} from '../../common/directives/permission-guard.directive';
import {PluralPipe} from '../../common/pipes/plural.pipe';

@Component({
  standalone: true,
  selector: 'mc-search-site',
  templateUrl: './search-site.component.html',
  styleUrls: ['./search-site.component.scss'],
  imports: [
    WalkThroughStepDirective,
    SearchInputComponent,
    ReactiveFormsModule,
    BlurOnComponentDirective,
    TooltipPanelWrapperComponent,
    TooltipPanelComponent,
    NgClass,
    ProgressSpinnerComponent,
    SiteListComponent,
    BtnComponent,
    PermissionGuardDirective,
    forwardRef(() => SearchSiteComponent),
    forwardRef(() => SearchSiteComponent),
  ],
})
@AutoUnsubscribe()
export class SearchSiteComponent implements OnInit {
  mySitesService = inject(MySitesService);
  mySitesApiService = inject(MySitesApiService);
  private elementRef = inject(ElementRef);
  private router = inject(Router);
  private sitesQuery = inject(SitesQuery);
  private amplitudeManager = inject(AmplitudeManager);
  private amplitudeService = inject(AmplitudeService);
  private paginationService = inject(PaginationService);

  @Input() isAgency: boolean;
  public showTooltip: boolean = false;
  searchValue: SiteSearchEntryDto[] = [];
  latestSites: SiteSearchEntryDto[] = [];
  searchString: string = '';
  loading: boolean = false;
  @Input() placeholder: string = 'My Sites...';

  searchSiteName: FormControl = new FormControl();
  get UserPermissions() {
    return UserPermissions;
  }

  // Text for the role="status" live region so screen readers announce suggestions without focus moving
  get statusMessage(): string {
    if (!this.showTooltip || this.loading) {
      return '';
    }
    const pluralize = new PluralPipe().transform;
    const count = this.searchString ? this.searchValue.length : this.latestSites.length;
    if (!count) {
      return this.searchString ? 'No results found' : '';
    }
    return `${count} ${pluralize(count, 'result', 'results')} available`;
  }

  @HostListener('document:mousedown', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    const targetElement = event.target as HTMLElement;

    if (targetElement && !this.elementRef.nativeElement.contains(targetElement)) {
      this.closeTooltip(event);
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDownHandler(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeTooltip(event);
    }
  }

  ngOnInit(): void {
    this.sitesQuery.activeSites$.subscribe((siteList: SitesQueryResponse) => {
      this.latestSites = siteList.sites.slice(0, 2);
    });
    this[SUBSCRIPTIONS].add(
      this.searchSiteName.valueChanges.pipe(distinctUntilChanged()).subscribe(value => {
        if (this.searchSiteName.touched) {
          this.searchSites(value);
        }
      }),
    );
  }

  searchSites(event: Event | string): void {
    if (!event['target']) {
      this.loading = true;
      this.showTooltip = true;
      this.searchString = event as string;
      const requestData: UwV2SitesSearchListParams = {
        s: this.searchString,
        p: 1,
        q: 2,
        status: ISiteStatus.ACTIVE,
      };
      this.mySitesApiService
        .mySites(requestData)
        .pipe(finalize(() => (this.loading = false)))
        .subscribe((response: SiteSearchResultDto) => {
          this.searchValue = response.sites || [];
        });
    }
  }

  openTooltip() {
    this.showTooltip = true;
  }

  closeTooltip(event: Event) {
    event.stopPropagation();
    this.showTooltip = false;
  }

  openAddSitesDrawer(event: Event) {
    this.amplitudeManager.setAddSiteProperties({
      addSitesTriggeredPlace: 'sites-search',
      addSiteTriggeredPage: this.amplitudeService.getCurrentPage(),
    });
    this.closeTooltip(event);
    this.mySitesService.forwardedSiteName = this.searchString.trim();
    void this.router.navigate([
      `widget/${this.paginationService.currentTab}/p/${this.paginationService.currentPage}/add-sites`,
    ]);
  }

  redirectToInspector() {
    window.open(environment.inspectorUrl + this.searchString, '_blank');
  }

  // Using for close tooltip panel when user exit by tab from it
  blurInput(event: FocusEvent) {
    if (event?.relatedTarget && !this.elementRef.nativeElement.contains(event.relatedTarget)) {
      this.showTooltip = false;
    }
  }
}
